import {
  InfoCard,
  Link,
  PasswordCriterias,
  Form,
  InputMask,
  InputPassword,
  useFormConfig,
  Button,
  FormAction,
  FormBloc,
  FormField,
  FormGrid,
  FormNotice,
  FormRow,
  FormTitle,
  displayErrorToast,
} from "@endya-conformite/ui-kit";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import { GatewayAuthApi } from "@src/data/auth/auth.api";
import { handleGatewayError } from "@src/data/utils/handleGatewayError";
import { Navigate, useNavigate, useOutletContext } from "react-router-dom";
import { useState } from "react";
import { ForgotPasswordOutletContext } from "./ForgotPassword";
import style from "./ForgotPasswordReset.module.scss";

const schema = zod.object({
  code: zod.string().nonempty(),
  password: zod
    .object({
      password: zod
        .string()
        .nonempty()
        .regex(
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/,
          "Votre mot de passe n'est pas assez sécurisé, vous devez avoir au moins 8 caractères dont 1 majuscule, 1 minuscule, 1 chiffre et un caractère spécial"
        ),
      confirm: zod.string().nonempty(),
    })
    .refine((data) => data.password === data.confirm, {
      message:
        "La confirmation de votre mot de passe ne correspond pas à la valeur du champ Mot de passe",
      path: ["confirm"],
    }),
});

export type ForgotPasswordResetFormValue = zod.infer<typeof schema>;

export function ForgotPasswordReset() {
  const navigate = useNavigate();
  const { data } = useOutletContext<ForgotPasswordOutletContext>();
  const formConfig = useFormConfig<typeof schema, ForgotPasswordResetFormValue>(
    {
      schema,
    }
  );
  const passwordValue = formConfig.watch("password.password");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (
    submittedData: ForgotPasswordResetFormValue
  ): Promise<void> => {
    setIsSubmitting(true);
    try {
      await GatewayAuthApi.confirmForgotPassword(
        data.email,
        submittedData.code,
        submittedData.password.password
      );
      navigate("/forgot-password/success");
    } catch (err) {
      handleGatewayError({
        onResponse: (error) => {
          displayErrorToast(
            error.response.data.message ||
              "Une erreur est survenue lors de la réinitialisation du mot de passe. Veuillez réessayer."
          );
        },
        onUnhandled: () => {
          displayErrorToast(
            "Impossible de contacter le serveur. Veuillez vérifier votre connexion internet et réessayer."
          );
        },
      })(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!data.email) return <Navigate to="/login" />;

  return (
    <Form formConfig={formConfig} onSubmit={handleSubmit}>
      <FormGrid disableHorizontalGutter>
        <FormTitle>
          <h1>Mot de passe oublié</h1>
        </FormTitle>
        <FormRow>
          <FormField>
            <InfoCard type="info">
              Un mail vous a été envoyé à l&apos;adresse <b>{data.email}</b>
              <br />
              Il contient le code de confirmation à renseigner ci-dessous.
            </InfoCard>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <FormNotice>
              Choisissez un nouveau mot de passe pour votre compte.
            </FormNotice>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <InputMask<ForgotPasswordResetFormValue>
              mask="999999"
              name="code"
              label="Code de confirmation"
              required
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <InputPassword<ForgotPasswordResetFormValue>
              name="password.password"
              label="Nouveau mot de passe"
              required
            />
          </FormField>
        </FormRow>
        <FormBloc>
          <PasswordCriterias password={passwordValue} />
        </FormBloc>
        <FormRow>
          <FormField>
            <InputPassword<ForgotPasswordResetFormValue>
              name="password.confirm"
              label="Confirmez votre mot de passe"
              required
            />
          </FormField>
        </FormRow>
        <FormAction className={style.actions}>
          <Link to="/login">
            <Button type="button" variant="text">
              Annuler
            </Button>
          </Link>
          <Button
            type="submit"
            disabled={isSubmitting}
            icon={isSubmitting ? <Loader /> : undefined}
          >
            Enregistrer le mot de passe
          </Button>
        </FormAction>
      </FormGrid>
    </Form>
  );
}
